import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import { Feature } from "models/api";
import { IEmployeeWorkshop, IEmployeeWorkshopData, IEmployeeWorkshopInput } from "models/workshop";
import React, { useEffect, useState } from "react";
import EventIcon from '@mui/icons-material/Event';
import { editEmployeeWorkshop, enrollEmployee, getEmployeeWorkshops } from "framework/workshops";
import BlockIcon from "@mui/icons-material/Block";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useFetchSection from "hooks/useFetchSection";
import { UserRoleName } from "types/auth";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Search from "components/Search/Search";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "react-query";
import { useAlert } from "contexts/alertContext";
import { handleError } from "helpers/utils";
import DateDisplay from "./partials/DateDisplay";
import DateDisplay2 from "./partials/DateDisplay2";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EmployeeWorkshopCard from "./EmployeeWorkshopCard";
import { deleteWaitingList, withdraw } from "framework/enrollment";
interface Props {
  feature: Feature;
}

const EmployeeWorkshop: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false)
  const [expanded, setExpanded] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const handleToggle = () => {

    setExpanded(!expanded);
  };
  const theme = useTheme();

  const [FilterWorkshops, setFilterWorkshops] = useState<IEmployeeWorkshop>(
    {} as IEmployeeWorkshop
  );
  const { setAlert } = useAlert();
  const [activeTab, setActiveTab] =
    useState<keyof IEmployeeWorkshop>("enrolledWorkshops");

  const userData = getUserData();
  const { colors, roleName } = userData;


  const enrollEmployeeMutation = useMutation({
    mutationFn: (workshopId: string) => {
      return enrollEmployee({
        workshopId,
        status: 0,
      });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (workshopId: string) => {
      return withdraw(
        workshopId
      );
    },
  });

  const deleteWaitingListMutation = useMutation({
    mutationFn: (waitingListId: string) => {
      return deleteWaitingList(
        waitingListId
      );
    },
  });


  const invitationWorkshopMutation = useMutation({
    mutationFn: (data: { createInput: IEmployeeWorkshopInput }) => {
      return editEmployeeWorkshop(data.createInput, data.createInput.id);
    },
  });


  const tabs: { label: string; value: WorkshopKey }[] = [
  { label: t("T_enrolled"), value: "enrolledWorkshops" },
  { label: t("T_waiting_list"), value: "waitingListWorkshops" },
  { label: t("T_completed"), value: "completedWorkshops" },
  { label: t("T_upcoming"), value: "openWorkshops" },
  { label: t("T_invitation"), value: "invitedWorkshops" },
];

  
  const {
    loading,
    data: EmplyeeWorkshops,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getEmployeeWorkshops(),
    fetchPermission: () => getPermission(6),
    section: "Workshops",
    onSuccess: (data) => setFilterWorkshops(data),
  });

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") as WorkshopKey | null;    
    if (tabFromUrl && tabs.some((t) => t.value === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else {
      setActiveTab(tabs[0].value);
      setSearchParams({ tab: tabs[0].value }, { replace: true });
    }
  }, [searchParams,setSearchParams]);

  if (loading || loading2)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (roleName !== UserRoleName.EMPLOYEE) return null;

  const handleSearch = (value: string) => {
    if (!value) return setFilterWorkshops(EmplyeeWorkshops!);
    let filtered = EmplyeeWorkshops![activeTab]!.filter((workshop) =>
      workshop.companyName.toLowerCase().includes(value.toLowerCase())
    );
    setFilterWorkshops((prev) => {
      return {
        ...prev,
        [activeTab]: filtered,
      };
    });
  };

  const handleChangeTab = (tab: string) => {
    setActiveTab(tab as keyof IEmployeeWorkshop);
    searchParams.set("tab", tab);
    setSearchParams(searchParams);
  };


  const handleEnroll = async (workshopId: string) => {
    try {
      setLoading2(true);
      const res = await enrollEmployeeMutation.mutateAsync(workshopId);
      setAlert({
        open: true,
        message: handleError(res),
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  }

  const handleWithdraw = async (workshopId: string) => {
    try {
      setLoading2(true);
      const res = await withdrawMutation.mutateAsync(workshopId);
      setAlert({
        open: true,
        message: handleError(res),
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  }

  const handleDeleteWaitingList = async (waitingListId: string) => {
    try {
      setLoading2(true);
      const res = await deleteWaitingListMutation.mutateAsync(waitingListId);
      setAlert({
        open: true,
        message: handleError(res),
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  }

  const handleSendInvitations = async (id: string, status: number) => {
    try {
      setLoading2(true);
      const res = await invitationWorkshopMutation.mutateAsync({
        createInput: {
          id,
          rejectionReason: '',
          status,
          workshopId: null,
        },
      });
      setAlert({
        open: true,
        message: handleError(res),
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  type WorkshopKey = keyof IEmployeeWorkshop;

  return (
    <>
      <Stack spacing={2} p={2}>
        <Search handleChange={handleSearch} />
        <Typography
          variant="h3"
          color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          sx={{ marginBottom: "16px", marginTop: "4px" }}
        >
          {t(feature.name)}
        </Typography>
        <Box
          sx={{ padding: "16px", backgroundColor: "#f5f5f5", width: "100%" }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginBottom: "16px"
            }}
          >
            {tabs.map((tab) => (
              <Button
                key={tab.value}
                variant={activeTab === tab.value ? "contained" : "outlined"}
                onClick={() => handleChangeTab(tab.value)}
                sx={{
                  flex: "1 1 calc(50% - 8px)", // Responsive width: 50% minus gap on small screens
                  minWidth: "120px", // Ensure a minimum width for smaller screens
                  textTransform: "none",
                  alignItems:'center',
                  gap:1,
                  borderRadius: "20px",
                  [theme.breakpoints.up("sm")]: {
                    flex: "1", // Full width buttons revert to flex:1 on medium+ screens
                  },
                }}
              ><div>{tab.label}</div> 
  <Box
    sx={{
      backgroundColor: activeTab === tab.value ? "white" : "primary.main",
      color: activeTab === tab.value ? "primary.main" : "white",
      borderRadius: "50%",
      width: 20,
      height: 20,
      fontSize: "0.75rem",
      display: "flex",
      alignItems: "center",
      gap:5,
      justifyContent: "center",
      fontWeight: "bold",
    }}
  >
    {FilterWorkshops[tab.value].length}
  </Box>
              </Button>
            ))}
          </Box>


          <List>
            {FilterWorkshops[activeTab].map(
              (workshop: IEmployeeWorkshopData, index: number) => (
                <EmployeeWorkshopCard activeTab={activeTab} handleEnroll={handleEnroll} handleWithdraw={handleWithdraw} handleDeleteWaitingList={handleDeleteWaitingList} workshop={workshop} handleSendInvitations={handleSendInvitations} />
              )
            )}
          </List>
        </Box>

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>
    </>
  );
};

export default EmployeeWorkshop;
